<script setup lang="ts">
/**
 * UI-kit component
 * Category: Inputs
 * Name: 'Inputs'
 * FIGMA: 'https://www.figma.com/design/CFyOwQUjj7uVRQTc03muvG/Demo-CS?node-id=7681-4708&m=dev'
 */
import { toRaw, watch } from 'vue'
import {
  required as requiredRule,
  email as emailRule,
} from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import type { ChangeEvent } from '@brighterly/lib-core-types/ui/inputs/interfaces'
import type { InputMode } from '@brighterly/lib-core-types/ui/inputs/types'
import type { IconDefinition as SolidIcon } from '@fortawesome/pro-solid-svg-icons'
import type { IconDefinition as RegularIcon } from '@fortawesome/pro-regular-svg-icons'

import { DefaultIcon } from '@shared/ui/icon'

defineOptions({
  name: 'InputPrimary',
})

const props = withDefaults(defineProps<InputPrimaryProps>(), {
  value: '',
  label: '',
  placeholder: '',
  type: 'text',
  mode: 'normal',
  errorMessage: null,
  maxlength: undefined,
  disabled: false,
  icon: undefined,
  required: false,
})

const emit = defineEmits<{
  change: [ChangeEvent]
  input: [ChangeEvent]
  'keyup:enter': [ChangeEvent]
}>()

interface InputPrimaryProps {
  id: string
  name: string
  value?: string
  label?: string
  placeholder?: string
  type?: 'text' | 'email' | 'textArea'
  mode?: InputMode
  errorMessage?: string | null
  maxlength?: string | undefined
  disabled?: boolean
  icon?: SolidIcon | RegularIcon
  required?: boolean
}

const state = reactive({
  model: props.value,
})

watch(() => props.value, (newValue) => {
  state.model = newValue
})

const rules = reactive({
  model: (props.type === 'email')
    ? { requiredRule, emailRule }
    : {},
})

const v$ = useVuelidate(rules, state)

const validateEmail = (): boolean => {
  v$.value.model.$validate()
  return !v$.value.model.$invalid
}

const validate = (): boolean => {
  return (props.type === 'email')
    ? validateEmail()
    : true
}

const inputValid = computed(() => validate())

const handleChange = (): void => {
  emit('change', {
    id: props.id,
    name: props.name,
    value: toRaw(state.model),
    valid: inputValid.value,
  })
}

const handleInput = (): void => {
  emit('input', {
    id: props.id,
    name: props.name,
    value: toRaw(state.model),
    valid: inputValid.value,
  })
}

const handleKeyupEnter = (): void => {
  emit('keyup:enter', {
    id: props.id,
    name: props.name,
    value: toRaw(state.model),
    valid: inputValid.value,
  })
}

</script>

<template>
  <div class="input-group">
    <label
      v-if="label"
      :for="id"
      class="input-group__label"
    >
      {{ label }}
      <span
        v-if="required"
        class="required-indicator"
      >
        *
      </span>
    </label>
    <div class="input-wrapper">
      <textarea
        v-if="type === 'textArea'"
        :id="id"
        v-model="state.model"
        :class="['input-group__input', mode, { 'with-icon': icon }]"
        :placeholder="placeholder"
        :disabled="disabled"
        @change="handleChange"
        @input="handleInput"
        @keyup.enter="handleKeyupEnter"
      />
      <input
        v-else
        :id="id"
        v-model="state.model"
        :name="name"
        :type="type"
        data-qa="quiz_input_field"
        :class="['input-group__input', mode, { 'with-icon': icon }]"
        autocomplete="off"
        :placeholder="placeholder"
        :maxlength="maxlength || undefined"
        :disabled="disabled"
        @change="handleChange"
        @input="handleInput"
        @keyup.enter="handleKeyupEnter"
      >
      <div
        v-if="icon"
        class="input-icon"
      >
        <DefaultIcon :icon="icon" />
      </div>
    </div>
    <div
      v-if="mode === 'error' && errorMessage"
      class="input-error"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<style scoped lang="scss" src="./InputPrimary.scss" />
